import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Avatar, Tooltip } from 'antd';
import CanvasConfetti from 'components/CanvasConfetti';
import SectionHeader from './SectionHeader';
import ProposalItemLoader from '../ProposalItemLoader';
import ProposalItem from '../ProposalItem';
import { useReactiveVar } from '@apollo/client';
import { userVar } from 'graphql/cache';
import { getSeenTimeDetails, getProposalTitle, getClientName } from 'helpers/proposal';
import { PROPOSAL_STATUS } from 'constants/index';

import './ApprovedProposals.scss';

const ApprovedProposals = ({
  approvedProposals,
  trash,
  isLoading,
  showEmptyMessage,
  tagWrapperWidth,
  setTagWrapperWidth,
  tagsData,
  setTagsData,
  onClickItem,
  proposalsVar,
  updateProposalTags,
  setIgnoreUpdates,
  tagsFilter,
  canLockUser,
  updateSignedSeenState,
  loadingMap,
  bulkLoading,
  haltAnimation,
  setLoadingMap,
}) => {
  const user = useReactiveVar(userVar);
  const auid = user?.teamId || user?._id;
  const proposalRefs = useRef([]);
  const [removedIds, setRemovedIds] = useState(new Set());

  const filteredApprovedProposals = useMemo(
    () => approvedProposals.filter((proposal) => !removedIds.has(proposal.pid)),
    [approvedProposals, removedIds]
  );

  useEffect(() => {
    setRemovedIds(new Set());
  }, [approvedProposals]);

  const limitToZero = (str) => {
    if (!str || !str.includes('-')) {
      return str;
    }
    str = `${str.split('-').slice(0, -1).join('')}0`;
  };

  if (bulkLoading) {
    return (
      <div className="loader-container">
        {Array.from({ length: 1 }, (_, x) => (
          <ProposalItemLoader key={x} />
        ))}
      </div>
    );
  }

  const handleSignedSeen = (ids) => {
    setRemovedIds((prev) => new Set([...prev, ...ids]));
    updateSignedSeenState(ids);
  };

  if (bulkLoading) return null;

  return (
    <div
      className={`approved-proposals-list ${filteredApprovedProposals.length < 1 ? 'hidden' : ''}`}>
      <SectionHeader
        filteredApprovedProposals={approvedProposals}
        bulkLoading={bulkLoading}
        handleSignedSeen={handleSignedSeen}
        setLoadingMap={setLoadingMap}
      />

      {filteredApprovedProposals.map((proposal, index) => {
        const { pid: proposalId, state = 'edit', tags, seenCount, openLogs } = proposal;
        const proposalTitle = getProposalTitle({ proposal, user });
        const clientName = getClientName({ proposal });
        let { minPrice, maxPrice } = proposal?.quotes || { minPrice: '$0', maxPrice: '$0' };

        minPrice = limitToZero(minPrice);
        maxPrice = limitToZero(maxPrice);

        const seenTime = getSeenTimeDetails({ proposal });

        let opened = seenCount || openLogs?.length;
        opened = opened && new Date(proposal.date) > new Date(2019, 5, 30) ? opened : null;

        return (
          <div key={index + proposalId} ref={(el) => (proposalRefs.current[index] = el)}>
            <CanvasConfetti
              event="APPROVED"
              delay={1000}
              targetElement={proposalRefs.current[index]}
              haltAnimation={haltAnimation}
            />
            <ProposalItem
              proposalIndex={index}
              tags={tags}
              name={proposalTitle}
              client={clientName}
              status={PROPOSAL_STATUS[state]}
              proposalId={proposalId}
              avatar={
                proposal.user ? (
                  <Tooltip title={proposal.user.profile.name}>
                    <Avatar src={proposal.user.profile.image} size={40}>
                      {proposal.user.profile.name?.slice(0, 2)}
                    </Avatar>
                  </Tooltip>
                ) : null
              }
              tagWrapperWidth={tagWrapperWidth}
              setTagWrapperWidth={setTagWrapperWidth}
              teamInfo={
                proposal.user
                  ? {
                      userId: user?._id,
                      userRole: user?.roles && user?.roles[0],
                      uid: proposal.uid,
                      auid: proposal.auid,
                    }
                  : null
              }
              pnum={proposal.pnum}
              trash={trash}
              watched={
                seenTime && new Date(proposal.date) > new Date(2019, 5, 30)
                  ? `${seenTime.minutes}:${seenTime.seconds} min`
                  : ''
              }
              opened={opened}
              emailLogs={proposal.emailLogs}
              lastSeen={proposal.lastSeen}
              clientIP={proposal?.signature?.clientIP}
              isSecured={proposal?.isSecured}
              quote={
                minPrice === maxPrice
                  ? `${minPrice || '$0'}`
                  : `${minPrice || '$0'} - ${maxPrice || '$0'}`
              }
              onClickItem={onClickItem}
              isLocked={canLockUser}
              variables={proposal?.draft?.variables}
              isDeletable={
                user?._id === proposal?.auid ||
                proposal.isOwner ||
                (user?.roles?.includes('manager') && !proposal.isManager)
              }
              auid={auid}
              pid={proposal._id}
              tagsData={tagsData}
              setTagsData={setTagsData}
              proposalsVar={proposalsVar}
              proposalList={approvedProposals}
              updateProposalTags={updateProposalTags}
              setIgnoreUpdates={setIgnoreUpdates}
              tagsFilter={tagsFilter}
              language={proposal?.language}
              isHighlighted={true}
              handleSignedSeen={handleSignedSeen}
              loadingMap={loadingMap}
            />
          </div>
        );
      })}
    </div>
  );
};

export default ApprovedProposals;
